import * as z from "zod";
import { collectFlowBindingExpressions, inspectFlowExpression } from "../contract/expression.js";
import type { FlowDefinition, FlowNode } from "./types.js";

/** Result of validating fields at a producer's consumed-output boundary. */
export interface NodeOutputValidationResult {
  /** Whether the fields may be exposed to downstream consumers. */
  valid: boolean;
  /** Stable, path-bearing failures. Empty when validation is not required. */
  issues: string[];
}

function consumerExpressions(node: FlowNode): string[] {
  const expressions = (node.contract?.requires ?? []).map(({ expr }) => expr);
  expressions.push(...collectFlowBindingExpressions(node.contract?.input));
  if (node.run?.kind === "router") {
    expressions.push(...node.run.routes.map(({ when }) => when));
  }
  return expressions;
}

function readsProducerOutput(node: FlowNode, producerId: string): boolean {
  return consumerExpressions(node).some((expression) =>
    inspectFlowExpression(expression, false).some(({ path }) =>
      path.startsWith(`nodes.${producerId}.output.fields.`),
    ),
  );
}

/**
 * Return a producer's declared fields schema only when another node reads it.
 *
 * Consumption is deliberately limited to preconditions, named bindings, and
 * router conditions. Status reads and unreferenced declarations remain prose-only.
 */
export function getConsumedOutputSchema(
  flow: FlowDefinition,
  producerId: string,
): Record<string, unknown> | undefined {
  const producer = flow.nodes.find(({ id }) => id === producerId);
  const schema = producer?.contract?.output?.fields;
  if (!schema) return undefined;
  return flow.nodes.some(
    (consumer) => consumer.id !== producerId && readsProducerOutput(consumer, producerId),
  )
    ? schema
    : undefined;
}

/** Validate proposed fields only when a downstream deterministic consumer requires them. */
export function validateConsumedNodeOutput(
  flow: FlowDefinition,
  producerId: string,
  fields: Record<string, unknown> | undefined,
): NodeOutputValidationResult {
  const schema = getConsumedOutputSchema(flow, producerId);
  if (!schema) return { valid: true, issues: [] };

  try {
    const validator = z.fromJSONSchema(schema as Parameters<typeof z.fromJSONSchema>[0]);
    const parsed = validator.safeParse(fields ?? {});
    if (parsed.success) return { valid: true, issues: [] };
    return {
      valid: false,
      issues: parsed.error.issues
        .map((issue) => {
          const suffix = issue.path.map(String).join(".");
          return `fields${suffix ? `.${suffix}` : ""}: ${issue.message}`;
        })
        .sort(),
    };
  } catch (error) {
    return {
      valid: false,
      issues: [
        `fields: invalid authored JSON Schema: ${
          error instanceof Error ? error.message : String(error)
        }`,
      ],
    };
  }
}
